import { Link } from "react-router-dom";
import * as React from "react";
import { ArrowRight, Cloud, LifeBuoy, MoreHorizontal, Server } from "lucide-react";

import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { DisasterRecoveryModal } from "./DisasterRecoveryModal";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import type { AutomationServer } from "@/data/automation-server";

const PROVISIONING_LABEL: Record<string, string> = {
  pending: "Waiting to start",
  creating_server: "Creating the machine",
  waiting_for_server: "Machine starting",
  creating_dns: "Setting up DNS",
  installing_software: "Installing Bailey",
  registering: "Registering",
  archiving_ssh_key: "Finishing up",
  failed: "Provisioning failed",
};

function since(iso: string) {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days < 1) return "today";
  if (days === 1) return "yesterday";
  if (days < 60) return `${days} days ago`;
  const months = Math.round(days / 30);
  if (months < 24) return `${months} months ago`;
  return `${Math.round(months / 12)} years ago`;
}

function Status({ server }: { server: AutomationServer }) {
  if (server.registration_pending) {
    return <Badge className="border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-50">Waiting for registration</Badge>;
  }
  const provisioning = server.server_type === "hetzner" && server.provisioning_status && server.provisioning_status !== "completed";
  if (provisioning) {
    const failed = server.provisioning_status === "failed";
    return (
      <Badge className={failed ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-50" : "border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-50"}>
        {PROVISIONING_LABEL[server.provisioning_status!] ?? server.provisioning_status}
      </Badge>
    );
  }
  return (
    <Badge className="gap-1.5 border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-50">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
      Connected
    </Badge>
  );
}

export function ServerTypeTile({ cloud, className = "" }: { cloud: boolean; className?: string }) {
  const Icon = cloud ? Cloud : Server;
  return (
    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${cloud ? "bg-sky-100 text-sky-700" : "bg-slate-100 text-slate-700"} ${className}`}>
      <Icon className="h-5 w-5" />
    </div>
  );
}

export function ServerRow({ server, highlighted, onOpenDetails }: { server: AutomationServer; highlighted: boolean; onOpenDetails?: (server: AutomationServer) => void }) {
  const cloud = server.server_type === "hetzner";
  const detail = `/automation-servers/${server.automation_server_id}`;
  const [recovering, setRecovering] = React.useState(false);

  return (
    <li
      className={`flex flex-wrap items-center gap-x-5 gap-y-3 px-5 py-4 transition-colors hover:bg-muted/30 ${highlighted ? "bg-emerald-50/70" : ""}`}
      data-testid={`server-row-${server.automation_server_id}`}
    >
      <ServerTypeTile cloud={cloud} />
      <div className="min-w-0 flex-1 basis-56">
        <div className="flex flex-wrap items-center gap-2">
          {onOpenDetails ? (
            <button
              type="button"
              onClick={() => onOpenDetails(server)}
              className="truncate text-base font-semibold leading-tight hover:underline"
              title={[server.automation_server_id, server.bailey_version && `Bailey ${server.bailey_version}`].filter(Boolean).join(" · ")}
            >
              {server.name}
            </button>
          ) : (
            <Link
              to={detail}
              className="truncate text-base font-semibold leading-tight hover:underline"
              title={[server.automation_server_id, server.bailey_version && `Bailey ${server.bailey_version}`].filter(Boolean).join(" · ")}
            >
              {server.name}
            </Link>
          )}
          <Status server={server} />
        </div>
        <p className="mt-0.5 truncate text-sm text-muted-foreground">
          {server.domain ?? (cloud ? "Private cloud server" : "Your own machine")}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
        <span className="text-sm text-muted-foreground" title={new Date(server.created_at).toLocaleString()}>added {since(server.created_at)}</span>
      </div>
      <div className="flex items-center gap-1.5">
        {server.bailey_url && !server.registration_pending && (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" aria-label="More actions" data-testid={`server-menu-${server.automation_server_id}`}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setRecovering(true)} data-testid={`server-recover-${server.automation_server_id}`}>
                  <LifeBuoy className="mr-2 h-4 w-4" />
                  Recover from backups
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DisasterRecoveryModal serverName={server.name} serverId={server.automation_server_id} open={recovering} onOpenChange={setRecovering} />
          </>
        )}
        {server.bailey_url && (onOpenDetails ? (
          <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => onOpenDetails(server)} data-testid={`server-details-${server.automation_server_id}`}>Details</Button>
        ) : (
          <Button asChild variant="ghost" size="sm" className="text-muted-foreground">
            <Link to={detail} data-testid={`server-details-${server.automation_server_id}`}>Details</Link>
          </Button>
        ))}
        {server.bailey_url ? (
          <Button asChild size="sm">
            <a href={server.bailey_url} target="_blank" rel="noopener noreferrer">
              Open Bailey console
            </a>
          </Button>
        ) : onOpenDetails ? (
          <Button size="sm" onClick={() => onOpenDetails(server)} data-testid={`server-details-${server.automation_server_id}`}>
            Details
            <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
        ) : (
          <Button asChild size="sm">
            <Link to={detail} data-testid={`server-details-${server.automation_server_id}`}>
              Details
              <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        )}
      </div>
    </li>
  );
}
